import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Loading } from '@monorepo/shared/components/Loading';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Description } from '@monorepo/shared/componentsV2/Description';
import { FieldTypePreviewFactory } from '@monorepo/shared/componentsV2/fieldDataType/FieldTypePreview/FieldTypePreviewFactory';
import { AnalyticsSyncDetailsSection } from '@monorepo/shared/componentsV2/fullPageWizard/AnalyticsSyncWizardStep/AnalyticsSyncDetailsSection';
import {
  Content,
  Page,
  SectionHeader,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { StartDate } from '@monorepo/shared/componentsV2/styled/header';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import {
  useLogEntryFields,
  useLogFormulaFields,
} from '@monorepo/shared/hooks/logs/useLogFields';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { format } from 'date-fns';
import { getCategoryDisplayString } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LogDateFieldPreview } from '../LogDateFieldPreview';
import {
  BackButton,
  ButtonContainer,
  ComplianceInput,
  FieldContainer,
  InstructionsContainer,
  StyledSection,
} from './styled';

export function LogDetail() {
  const { logId, organizationId } = useParams();
  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);
  const navigate = useNavigate();
  const { log, isLoading } = useLog({
    logId,
    organizationId,
    config: { useErrorBoundary: true },
  });
  const { logFields: logEntryFields } = useLogEntryFields({
    logId,
    organizationId,
  });
  const { logFields: logFormulaFields } = useLogFormulaFields({
    logId,
    organizationId,
  });

  // TODO: convert these to absolute links instead of relative links
  const handleBack = useCallback(() => navigate('..'), [navigate]);
  const handleEdit = useCallback(() => navigate('./edit'), [navigate]);

  if (!logId || !organizationId) {
    throw new Error(
      'LogDetail was rendered in a context without organization or/and log id',
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  // Should never happen, a contract with the BE is that it either returns a value or 404
  if (!log) {
    return null;
  }

  return (
    <Page>
      <Header
        centerActions={
          <StartDate>
            {`Created ${format(new Date(log.createdAt), 'P')}`}
          </StartDate>
        }
        leftActions={
          <BackButton onClick={handleBack}>
            <VisuallyHidden>Back</VisuallyHidden>
            <ArrowBackIcon />
          </BackButton>
        }
        rightActions={
          canEditOrgSettings ? (
            <ButtonContainer>
              <Button color="primary" onClick={handleEdit} variant="contained">
                Edit Log
              </Button>
            </ButtonContainer>
          ) : null
        }
        title={log.name}
      />
      <Content>
        <StyledSection>
          <SectionHeader>Compliance Area</SectionHeader>
          <ComplianceInput
            label="Compliance Area"
            isLabelHidden
            name="category"
            readOnly
            value={getCategoryDisplayString(log.category)}
            dontSaveSpaceForErrorText
          />
        </StyledSection>
        {log.description && log.description.trim().length > 0 && (
          <StyledSection>
            <SectionHeader>Description</SectionHeader>
            <Description>{log.description}</Description>
          </StyledSection>
        )}
        <AnalyticsSyncDetailsSection
          datasetOriginId={logId}
          organizationId={organizationId}
          datasetOriginLabel="Log"
        />
        <StyledSection>
          <SectionHeader>Log Fields</SectionHeader>
          {log.instructions && (
            <InstructionsContainer>{log.instructions}</InstructionsContainer>
          )}
          <FieldContainer>
            <LogDateFieldPreview key="log-date" />
            {logEntryFields?.map((entryField) => (
              <FieldTypePreviewFactory
                field={entryField}
                key={entryField.name}
              />
            ))}
          </FieldContainer>
        </StyledSection>
        {!!logFormulaFields?.length && (
          <StyledSection>
            <SectionHeader>Formulas</SectionHeader>
            <FieldContainer>
              {logFormulaFields?.map((formulaField) => (
                <FieldTypePreviewFactory
                  field={formulaField}
                  key={formulaField.name}
                />
              ))}
            </FieldContainer>
          </StyledSection>
        )}
      </Content>
    </Page>
  );
}
